'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import type { ProjectSummary } from '@/lib/dcma-scoring-engine';

interface ProjectSummaryCardProps {
  summary: ProjectSummary;
}

function StatItem({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="text-center">
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground/70">{sub}</p>}
    </div>
  );
}

export function ProjectSummaryCard({ summary }: ProjectSummaryCardProps) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold">Schedule Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Activity counts */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Activities
          </p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <StatItem
              label="Total Activities"
              value={summary.totalActivities}
            />
            <StatItem
              label="Task Dependent"
              value={summary.taskActivities}
            />
            <StatItem
              label="Milestones"
              value={summary.milestones}
              sub={`${summary.startMilestones} start / ${summary.finishMilestones} finish`}
            />
            <StatItem
              label="Level of Effort"
              value={summary.loeActivities}
            />
            <StatItem
              label="Non-LOE"
              value={summary.nonLOEActivities}
              sub="Used as denominator"
            />
            <StatItem
              label="Incomplete Non-LOE"
              value={summary.incompleteNonLOE}
              sub="Float/CP denominator"
            />
          </div>
        </div>

        <Separator />

        {/* Relationship counts */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Relationships
          </p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <StatItem
              label="Total Relationships"
              value={summary.totalRelationships}
            />
            <StatItem
              label="Finish-to-Start"
              value={summary.fsRelationships}
              sub={`${summary.totalRelationships > 0 ? ((summary.fsRelationships / summary.totalRelationships) * 100).toFixed(1) : 0}%`}
            />
            <StatItem
              label="Start-to-Start"
              value={summary.ssRelationships}
              sub={`${summary.totalRelationships > 0 ? ((summary.ssRelationships / summary.totalRelationships) * 100).toFixed(1) : 0}%`}
            />
            <StatItem
              label="Finish-to-Finish"
              value={summary.ffRelationships}
              sub={`${summary.totalRelationships > 0 ? ((summary.ffRelationships / summary.totalRelationships) * 100).toFixed(1) : 0}%`}
            />
            <StatItem
              label="With Lags"
              value={summary.relationshipsWithLag}
              sub={`${summary.totalRelationships > 0 ? ((summary.relationshipsWithLag / summary.totalRelationships) * 100).toFixed(1) : 0}%`}
            />
            <StatItem
              label="Rel. Ratio"
              value={Number(
                (summary.totalRelationships / (summary.nonLOEActivities || 1)).toFixed(2)
              )}
              sub={`${summary.totalRelationships} / ${summary.nonLOEActivities}`}
            />
          </div>
        </div>

        <Separator />

        {/* Status and key denominators */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Status Breakdown
          </p>
          <div className="flex flex-wrap items-center justify-start gap-6">
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full bg-success" />
              <span className="text-xs text-muted-foreground">Completed</span>
              <span className="text-sm font-semibold text-foreground">
                {summary.completedActivities}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full bg-primary" />
              <span className="text-xs text-muted-foreground">In Progress</span>
              <span className="text-sm font-semibold text-foreground">
                {summary.inProgressActivities}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
              <span className="text-xs text-muted-foreground">Not Started</span>
              <span className="text-sm font-semibold text-foreground">
                {summary.notStartedActivities}
              </span>
            </div>
            <Separator
              orientation="vertical"
              className="h-5 mx-1"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Incomplete Tasks</span>
              <span className="text-sm font-semibold text-foreground">
                {summary.incompleteTaskActivities}
              </span>
              <span className="text-[10px] text-muted-foreground/60">(duration denominator)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Hard Constrained</span>
              <span className="text-sm font-semibold text-foreground">
                {summary.activitiesWithConstraints}
              </span>
              <span className="text-[10px] text-muted-foreground/60">(MFO/MSO/SNLT/FNLT)</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
